// utils/coursePolling.js
import { UNIVERSITY_API_URL } from '../config';

/**
 * Polls a course until its content has been processed by the S3 Lambda
 * @param {string} courseId - The internal course ID
 * @param {number} maxAttempts - Maximum number of polling attempts
 * @param {number} intervalMs - Milliseconds between polls
 * @returns {Promise<Object>} The processed course data
 */
export const pollForCourseReady = async (
    courseId,
    maxAttempts = 30,
    intervalMs = 2000
) => {
    const token = JSON.parse(localStorage.getItem('user'))?.token;
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
            const response = await fetch(
                `${UNIVERSITY_API_URL}/universitycourse/${courseId}/?includes=`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to fetch course: ${response.statusText}`);
            }

            const result = await response.json();
            const course = result.data;

            // Check if courseContentPath has been populated by the Lambda
            if (course.courseContentPath && course.courseContentPath.length > 0) {
                console.log(`Course ${courseId} is ready after ${attempt + 1} attempts`);
                return course;
            }

            // Wait before next poll
            await new Promise(resolve => setTimeout(resolve, intervalMs));
            
        } catch (error) {
            console.error(`Polling attempt ${attempt + 1} failed:`, error);
            
            // If it's the last attempt, throw the error
            if (attempt === maxAttempts - 1) {
                throw error;
            }
            
            // Otherwise, wait and try again
            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }
    }

    throw new Error('Course processing timed out. Please refresh the page to check status.');
};

/**
 * Checks if a course has been fully processed
 * @param {string} courseId - The internal course ID
 * @returns {Promise<boolean>} True if course is ready
 */
export const isCourseReady = async (courseId) => {
    try {
        const token = JSON.parse(localStorage.getItem('user'))?.token;
        
        const response = await fetch(
            `${UNIVERSITY_API_URL}/universitycourse/${courseId}/?includes=`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            }
        );

        if (!response.ok) {
            return false;
        }

        const result = await response.json();
        return !!(result.data?.courseContentPath);
        
    } catch (error) {
        console.error('Error checking course status:', error);
        return false;
    }
};